import type { ChangeEvent } from 'react';
import { useEffect, useId } from 'react';
import { slugify } from '../lib/slugify';
import { Tooltip, type TooltipValue } from './Tooltip';

export interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  description?: string;
  /** v6 — tooltip shown on hover/focus next to the label / swatch. */
  tooltip?: TooltipValue;
  /**
   * v7 — pixel-eyedropper mode. When `true`, the picker is armed and the
   * next click anywhere on the page is captured, the colour sampled from
   * `eyedropperImage`, and the result passed to `onEyedropSample`. Pressing
   * Escape cancels via `onEyedropCancel`.
   */
  eyedropperOpen?: boolean;
  /** v7 — the eye <img> element to sample pixels from. */
  eyedropperImage?: HTMLImageElement | null;
  /** v7 — fired when the eyedropper picks a colour. */
  onEyedropSample?: (hex: string) => void;
  /** v7 — fired when the user cancels (Escape, or arms off). */
  onEyedropCancel?: () => void;
}

/**
 * Read the pixel colour at the click position in the image's natural
 * coordinate space. Returns `#rrggbb` or null on failure / out-of-bounds.
 * Image must be same-origin so `getImageData` doesn't taint the canvas.
 */
export function samplePixelFromImage(
  img: HTMLImageElement,
  clientX: number,
  clientY: number,
): string | null {
  try {
    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const nx = (clientX - rect.left) / rect.width;
    const ny = (clientY - rect.top) / rect.height;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return null;
    const px = Math.min(img.naturalWidth - 1, Math.max(0, Math.floor(nx * img.naturalWidth)));
    const py = Math.min(img.naturalHeight - 1, Math.max(0, Math.floor(ny * img.naturalHeight)));
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    const data = ctx.getImageData(px, py, 1, 1).data;
    if (!data || data.length < 3) return null;
    const r = data[0] ?? 0;
    const g = data[1] ?? 0;
    const b = data[2] ?? 0;
    return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0').toUpperCase()).join('');
  } catch {
    return null;
  }
}

/**
 * Colour input with a ≥44px hit target.
 * v6 — tooltip on the label.
 * v7 — eyedropper mode: arms a global click listener that samples a pixel
 * from `eyedropperImage`. Escape cancels.
 */
export function ColorPicker(props: ColorPickerProps) {
  const { label, value, onChange, description, tooltip, eyedropperOpen = false, eyedropperImage, onEyedropSample, onEyedropCancel } = props;
  const id = `color-${slugify(label)}-${useId()}`;
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value);

  useEffect(() => {
    if (!eyedropperOpen || !eyedropperImage) return;
    const handler = (e: MouseEvent) => {
      const hex = samplePixelFromImage(eyedropperImage, e.clientX, e.clientY);
      if (hex) onEyedropSample?.(hex);
      onEyedropCancel?.();
    };
    const keyHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onEyedropCancel?.(); };
    window.addEventListener('mousedown', handler, true);
    window.addEventListener('keydown', keyHandler, true);
    const prev = document.body.style.cursor;
    document.body.style.cursor = 'crosshair';
    return () => {
      window.removeEventListener('mousedown', handler, true);
      window.removeEventListener('keydown', keyHandler, true);
      document.body.style.cursor = prev;
    };
  }, [eyedropperOpen, eyedropperImage, onEyedropSample, onEyedropCancel]);

  const labelNode = (
    <label htmlFor={id} className="text-sm font-medium text-neutral-800 dark:text-neutral-200">{label}</label>
  );

  return (
    <div className="flex items-center justify-between gap-3">
      {tooltip ? <Tooltip value={tooltip}>{labelNode}</Tooltip> : labelNode}
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className="block h-7 w-7 rounded-md border border-neutral-300 dark:border-neutral-700" style={{ backgroundColor: value }} />
        <input
          id={id} name={id} type="color" value={value} onChange={handleChange}
          aria-label={description ?? label} title={description ?? label}
          disabled={eyedropperOpen}
          className="h-11 w-11 cursor-pointer rounded-md border border-neutral-300 bg-transparent p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700"
        />
        <span aria-hidden="true" className="rounded-md bg-neutral-200 px-2 py-0.5 font-mono text-xs uppercase text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">{value}</span>
        {eyedropperOpen ? (
          <span role="status" aria-live="polite" data-testid="color-picker-eyedropper-armed" className="rounded-md border border-accent bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-accent">Click on the eye</span>
        ) : null}
      </div>
    </div>
  );
}
